"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Testimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  quote: string;
}
export default function CustomerVoices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const [api, setApi] = React.useState<CarouselApi>();
  const [hovering, setHovering] = React.useState(false);
  const autoplayPlugin = React.useRef(Autoplay({
    delay: 5000,
    stopOnInteraction: false,
    stopOnMouseEnter: true
  }));
  const testimonials: Testimonial[] = [{
    id: 1,
    name: "田中 健一",
    role: "代表取締役社長",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1411237/img/testimonial_tanaka_kenichi.png",
    quote: "Nhật Minh Khang & Partnersのサポートにより、ベトナム市場への進出がスムーズに実現しました。専門的なアドバイスと現地ネットワークのおかげで、想定以上の成果を上げることができています。"
  }, {
    id: 2,
    name: "佐藤 美咲",
    role: "事業開発部長",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1411237/img/testimonial_sato_misaki.png",
    quote: "投資から会社設立まで、一貫したサポートを受けることができました。ベトナムのビジネス環境に精通したチームの支援は、私たちにとって非常に心強いものでした。信頼できるパートナーです。"
  }];
  React.useEffect(() => {
    if (!api) return;
    const onMouseEnter = () => {
      autoplayPlugin.current.stop();
      setHovering(true);
    };
    const onMouseLeave = () => {
      autoplayPlugin.current.play();
      setHovering(false);
    };
    const container = api.containerNode();
    container.addEventListener("mouseenter", onMouseEnter);
    container.addEventListener("mouseleave", onMouseLeave);
    return () => {
      container.removeEventListener("mouseenter", onMouseEnter);
      container.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [api]);
  return <section ref={ref} className="relative bg-background py-24 overflow-hidden">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            お客様の声
          </h2>
          <div className="w-16 h-1 bg-accent mx-auto rounded-full" />
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} plugins={[autoplayPlugin.current]} setApi={setApi} className={`w-full transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <CarouselContent>
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                <div className="px-4">
                  <div className="card p-8 md:p-12 bg-background border border-border rounded-lg shadow-soft">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      <div className={`flex-shrink-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${hovering ? "scale-103" : "scale-100"}`}>
                        <SafeHydrate><Avatar className="w-24 h-24 md:w-32 md:h-32 ring-2 ring-accent/20">
                          <AvatarImage src={testimonial.image} alt={testimonial.name} className="object-cover" />
                          <AvatarFallback className="bg-primary-light text-primary text-2xl font-bold">
                            {testimonial.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                      </div>

                      <div className="flex-1 text-center md:text-left">
                        <blockquote className={`text-lg md:text-xl text-foreground leading-relaxed mb-6 font-sans transition-opacity duration-[var(--duration-slow)] ${hovering ? "opacity-100" : "opacity-90"}`}>
                          "{testimonial.quote}"
                        </blockquote>
                        <div className="flex flex-col gap-1">
                          <cite className="not-italic font-display font-bold text-foreground text-xl">
                            {testimonial.name}
                          </cite>
                          <span className="text-muted-foreground text-sm">
                            {testimonial.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>

        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, i) => <button key={i} onClick={() => api?.scrollTo(i)} className={`w-2 h-2 rounded-full transition-all duration-[var(--duration-medium)] ${api?.selectedScrollSnap() === i ? "bg-accent w-8" : "bg-border hover:bg-accent/50"}`} aria-label={`Go to testimonial ${i + 1}`} />)}
        </div>
      </div>
    </section>;
}